// src/components/JobcardModal.tsx
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import React from 'react';
import { CustomTask } from '../helper';
import { DateTime } from 'luxon';
import { verifyProgressPassword } from '../Api';
import { Password } from 'primereact/password';

interface JobcardModalProps {
    show: boolean;
    onHide: () => void;
    task: CustomTask | null;
    fetchData: () => void;
    handleTaskChange: (task: CustomTask) => void;
}

const JobcardModal: React.FC<JobcardModalProps> = ({ show, onHide, task, fetchData, handleTaskChange }) => {
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [progress, setProgress] = React.useState(0.0);
    const [loading, setLoading] = React.useState(false);
    const [isValid, setIsValid] = React.useState(false);
    const [locked, setLocked] = React.useState(true);
    const [errors, setErrors] = React.useState({
        startDate: '',
        endDate: '',
        dateRange: '',
    });

    // Password dialog state
    const [showPasswordDialog, setShowPasswordDialog] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState('');
    const [passwordLoading, setPasswordLoading] = React.useState(false);

    // Track whether user wants to lock or unlock
    const passwordActionRef = React.useRef<'lock' | 'unlock' | null>(null);

    // Handle lock/unlock button click
    const toggleLock = () => {
        if (locked) {
            // Show prompt to unlock
            setShowPasswordDialog(true);
            setPassword('');
            setPasswordError('');
            passwordActionRef.current = 'unlock';
        } else {
            // Show prompt to lock
            setShowPasswordDialog(true);
            setPassword('');
            setPasswordError('');
            passwordActionRef.current = 'lock';
        }
    };

    const handlePasswordSubmit = async () => {
        if (!password) return;

        setPasswordError('');
        setPasswordLoading(true);
        try {
            const isValid = await verifyProgressPassword(password);
            if (!isValid) {
                setPasswordError('Incorrect password. Please try again.');
                return;
            }

            if (passwordActionRef.current === 'unlock') {
                setLocked(false);
            } else if (passwordActionRef.current === 'lock') {
                setLocked(true);
            }
            setShowPasswordDialog(false);
            setPassword('');
            passwordActionRef.current = null;
        } catch (error) {
            console.error('Error verifying progress password:', error);
            setPasswordError('Unable to verify password. Please try again.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handlePasswordCancel = () => {
        setShowPasswordDialog(false);
        setPassword('');
        setPasswordError('');
        passwordActionRef.current = null;
    };

    React.useEffect(() => {
        validateDates();
    }, [startDate, endDate]);

    const validateDates = () => {
        const start = DateTime.fromISO(startDate);
        const end = DateTime.fromISO(endDate);
        let newErrors = {
            startDate: '',
            endDate: '',
            dateRange: '',
        };
        let valid = true;

        if (!startDate) {
            newErrors.startDate = 'Start date is required.';
            valid = false;
        } else if (!start.isValid) {
            newErrors.startDate = 'Invalid start date.';
            valid = false;
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required.';
            valid = false;
        } else if (!end.isValid) {
            newErrors.endDate = 'Invalid end date.';
            valid = false;
        }

        if (start.isValid && end.isValid) {
            if (start > end) {
                newErrors.dateRange = 'Start date cannot be after end date.';
                valid = false;
            } else if (end > start.plus({ years: 1 })) {
                newErrors.dateRange = 'Please enter a valid date range';
                valid = false;
            }
        }

        setErrors(newErrors);
        setIsValid(valid);
    };

    React.useEffect(() => {
        if (task) {
            const taskIsNew = task.taskdateFlag;
            if (!taskIsNew) {
                const formatDate = (date: Date) =>
                    DateTime.fromJSDate(date).toFormat('yyyy-MM-dd');
                setStartDate(formatDate(task.start));
                setEndDate(formatDate(task.end));
                setProgress(task.progress);
            } else {
                setStartDate('');
                setEndDate('');
                setProgress(0);
            }
            setLocked(task.is_locked ?? true);
        }
    }, [task]);

    const handleSubmit = async () => {
        if (!isValid || !task) return;
        setLoading(true);
        try {
            // Construire un CustomTask avec les nouvelles dates (en Date JS)
            const newStart = DateTime.fromFormat(startDate, 'yyyy-MM-dd')
                .startOf('day')
                .toJSDate();
            const newEnd = DateTime.fromFormat(endDate, 'yyyy-MM-dd')
                .endOf('day')
                .toJSDate();

            const updatedTask: CustomTask = {
                ...task,
                start: newStart,
                end: newEnd,
                progress,
                is_locked: locked,
            };

            // Laisser App.handleTaskChange gérer dépendances + backend + rechargement
            handleTaskChange(updatedTask);
        } catch (error) {
            console.error('Error preparing task update:', error);
        } finally {
            onHide();
            setLoading(false);
        }
    };

    const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let sanitizedValue = value.match(/^\d{0,3}(\.\d{0,2})?$/) ? value : progress.toString();
        let numericValue = parseInt(sanitizedValue);
        if (numericValue < 0) numericValue = 0;
        if (numericValue > 100) numericValue = 100;
        setProgress(numericValue);
    };

    return (
        <>
            <Dialog
                header={`${task?.taskdateFlag ? 'Set Date' : 'Edit'} ${task?.name}`}
                visible={show}
                style={{ width: '50vw' }}
                onHide={() => !loading && onHide()}
                footer={
                    <>
                        <Button
                            label={loading ? 'Submitting...' : 'Submit'}
                            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                            onClick={handleSubmit}
                            disabled={!isValid || loading}
                        />
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={onHide}
                            className="p-button-danger"
                            disabled={loading}
                        />
                    </>
                }
            >
                <div className="p-fluid p-grid gap-2">
                    {/* Start Date */}
                    <div className="p-field p-grid">
                        <div className="p-col-3 p-text-right flex align-items-center">
                            <label htmlFor="startDate">Planned Start Date</label>
                        </div>
                        <div className="p-col-9">
                            <input
                                id="startDate"
                                type="date"
                                className="p-inputtext p-component w-full"
                                value={startDate}
                                max={endDate || undefined}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        {errors.startDate && (
                            <div style={{ color: 'red' }}>{errors.startDate}</div>
                        )}
                    </div>

                    {/* End Date */}
                    <div className="p-field p-grid">
                        <div className="p-col-3 p-text-right flex align-items-center">
                            <label htmlFor="endDate">Planned End Date</label>
                        </div>
                        <div className="p-col-9">
                            <input
                                id="endDate"
                                type="date"
                                min={startDate || undefined}
                                className="p-inputtext p-component w-full"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        {errors.endDate && (
                            <div style={{ color: 'red' }}>{errors.endDate}</div>
                        )}
                        {errors.dateRange && (
                            <div style={{ color: 'red' }}>{errors.dateRange}</div>
                        )}
                    </div>

                    {/* Progress In % */}
                    {!task?.taskdateFlag && (
                        <div className="p-field p-grid">
                            <div className="p-col-3 p-text-right flex align-items-center">
                                <label htmlFor="progress">Progress In %</label>
                            </div>
                            <div className="p-col-7">
                                <input
                                    id="progress"
                                    type="number"
                                    className="p-inputtext p-component w-full"
                                    value={progress}
                                    onChange={handleProgressChange}
                                    min="0"
                                    max="100"
                                    step="1"
                                    disabled={locked}
                                />
                            </div>
                            <div className="p-col-2 flex justify-content-end align-items-center">
                                <Button
                                    icon={locked ? 'pi pi-lock' : 'pi pi-lock-open'}
                                    className={`p-button-rounded p-button-text ${
                                        locked ? 'p-button-secondary' : 'p-button-success'
                                    }`}
                                    onClick={toggleLock}
                                    tooltip={locked ? 'Unlock to edit progress' : 'Lock progress'}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Password Modal */}
            <Dialog
                header="Enter Password"
                visible={showPasswordDialog}
                style={{ width: '400px' }}
                onHide={handlePasswordCancel}
                footer={
                    <>
                        <Button
                            label="Submit"
                            icon="pi pi-check"
                            onClick={handlePasswordSubmit}
                            disabled={!password || passwordLoading}
                        />
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={handlePasswordCancel}
                            className="p-button-secondary"
                            disabled={passwordLoading}
                        />
                    </>
                }
            >
                <div className="p-fluid">
                    <div className="p-field">
                        <label htmlFor="password">Password</label>
                        <Password
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            feedback={false}
                            toggleMask
                            className="w-full"
                            placeholder="Enter password"
                            onKeyPress={(e) => e.key === 'Enter' && !passwordLoading && handlePasswordSubmit()}
                            disabled={passwordLoading}
                        />
                        {passwordError && (
                            <div style={{ color: 'red', marginTop: '5px' }}>{passwordError}</div>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default JobcardModal;